import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  onAuthStateChanged, 
  User as FirebaseUser 
} from 'firebase/auth';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { UserProfile } from '../types';

interface AuthContextType {
  user: FirebaseUser | null;
  profile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  isAdmin: false,
});

/**
 * 🔐 AuthProvider
 * Standalone identity engine. It bridges Firebase Auth with 
 * our custom Firestore 'users' collection.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 1. Listen for Firebase Auth changes (Session)
    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);

      if (!firebaseUser) {
        setProfile(null);
        setLoading(false);
      } else {
        // Last-known-platform stamp, read by usage-analytics capture (functions/src/lib/
        // captureInteraction.ts) to attribute interactions to web vs. mobile. Also feeds the
        // admin panel's existing (previously dead) getVersionDistribution action.
        setDoc(
          doc(db, 'users', firebaseUser.uid),
          { platform: 'web', lastPlatformSeenAt: serverTimestamp() },
          { merge: true }
        ).catch((err) => console.error('Failed to stamp platform on session start:', err));
      }
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    // 2. Listen for Firestore Profile changes (Metadata)
    if (user?.uid) {
      const userDocRef = doc(db, 'users', user.uid);
      
      const unsubscribeProfile = onSnapshot(userDocRef, (docSnap) => {
        if (docSnap.exists()) {
          setProfile({
            id: docSnap.id,
            ...docSnap.data()
          } as UserProfile);
        }
        setLoading(false);
      }, (error) => {
        console.error("Profile Sync Error:", error);
        setLoading(false);
      });

      return () => unsubscribeProfile();
    }
  }, [user]);

  const isAdmin = profile?.id === 'admin' || false; // Simple logic, adjust as needed

  return (
    <AuthContext.Provider value={{ user, profile, loading, isAdmin }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

/**
 * 🛰️ useAuth Hook
 * Access current user identity and profile metadata anywhere in the app.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};